import { useEffect, useState } from 'react';
import type {
  LeagueDifferentialPick,
  LeagueInsightPlayerStat,
  LeagueInsights as LeagueInsightsData,
  LeagueStandingsPage,
  LeagueTemplateScore,
} from '@fantasy/types';
import { ApiRequestError, api } from '../api.ts';
import { Term } from './Term.tsx';

interface LeagueInsightsProps {
  leagueId: number;
  leagueName: string | undefined;
  /** Page 1 of this league's standings, already fetched by `LeagueStandings`
   * — read only to tell whether the league has more members than the read
   * below is capped to, never re-fetched here. */
  standings: LeagueStandingsPage | null;
}

/** How many rivals a stat's count is out of, in the manager's own words —
 * "9 of the 12 rivals we could read", never a bare percentage. */
function rivalsPhrase(count: number, rivalsRead: number): string {
  return `${count} of the ${rivalsRead} rival${rivalsRead === 1 ? '' : 's'} we could read`;
}

/**
 * T-091's league read (mini-league template, biggest differential, and every
 * read manager's distance from consensus) surfaced beside the standings
 * table it describes — the most shareable screen in the app, so every
 * figure states its own denominator rather than a bare percentage, and a
 * read with nothing to say (a one-manager league, every rival fetch having
 * failed) says so instead of rendering zeroes.
 */
export function LeagueInsights({ leagueId, leagueName, standings }: LeagueInsightsProps) {
  const [insights, setInsights] = useState<LeagueInsightsData | null>(null);
  const [gameweek, setGameweek] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getLeagueInsights(leagueId)
      .then(({ insights: fetched, gameweek: fetchedGameweek }) => {
        if (cancelled) return;
        setInsights(fetched);
        setGameweek(fetchedGameweek);
      })
      .catch((err) => {
        if (!cancelled) {
          setInsights(null);
          setError(err instanceof ApiRequestError ? err.message : "Could not load this league's insights.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  if (loading) {
    return (
      <p className="hint mt-lg" aria-live="polite">
        Reading the room…
      </p>
    );
  }

  if (error) {
    return (
      <ul className="notices notices--flush mt-lg">
        <li className="error">{error}</li>
      </ul>
    );
  }

  if (!insights) return null;

  // The rivals this read is scoped to are always drawn from standings page
  // 1, which FPL's page size (50) always covers in full — see
  // effectiveOwnership.ts. So a page-1 read longer than the cap, or a
  // second page existing at all, both mean the league has more members than
  // the top N this read actually covers.
  const leagueLargerThanCap =
    standings !== null &&
    (standings.entries.length > insights.rivalCap || standings.hasNext || standings.page > 1);

  return (
    <div className="league-insights mt-lg">
      <h4>Insights{leagueName ? ` for ${leagueName}` : ''}</h4>

      {insights.rivalsRead === 0 ? (
        <p className="hint">
          {`Nobody else in this league could be read${gameweek ? ` for gameweek ${gameweek}` : ' this gameweek'} — could be a one-manager league, or every rival's picks failed to load. Nothing to compare yet.`}
        </p>
      ) : (
        <>
          <p className="hint">
            {`We read ${insights.rivalsRead} of the top ${insights.rivalCap} rivals in this league${gameweek ? ` for gameweek ${gameweek}` : ''}${
              leagueLargerThanCap
                ? ` — this is the top ${insights.rivalCap} of the table, not the whole league.`
                : '.'
            }`}
          </p>

          <div className="stats">
            <PlayerStatCallout label="Most owned" verb="Owned by" stat={insights.mostOwned} />
            <PlayerStatCallout label="Most captained" verb="Captained by" stat={insights.mostCaptained} />
            <DifferentialCallout differential={insights.differential} />
          </div>

          <TemplateSpread scores={insights.templateScores} />
        </>
      )}
    </div>
  );
}

interface PlayerStatCalloutProps {
  label: string;
  verb: string;
  stat: LeagueInsightPlayerStat | null;
}

function PlayerStatCallout({ label, verb, stat }: PlayerStatCalloutProps) {
  return (
    <dl className="callout">
      <dt>{label}</dt>
      <dd>
        {stat ? (
          <>
            <strong>{stat.name}</strong>
            <div className="hint">
              {verb} {rivalsPhrase(stat.count, stat.rivalsRead)} ({stat.percent}%)
            </div>
          </>
        ) : (
          <span className="hint">Nothing to report yet.</span>
        )}
      </dd>
    </dl>
  );
}

function DifferentialCallout({ differential }: { differential: LeagueDifferentialPick | null }) {
  return (
    <dl className="callout">
      <dt>
        Biggest <Term term="differential">differential</Term>
      </dt>
      <dd>
        {differential ? (
          <>
            <strong>{differential.name}</strong>
            <div className="hint">
              {`Owned by ${rivalsPhrase(differential.ownedBy, differential.rivalsRead)} (${differential.ownedPercent}%), form ${differential.form}`}
            </div>
          </>
        ) : (
          <span className="hint">Nothing to report yet.</span>
        )}
      </dd>
    </dl>
  );
}

/** Every read manager's distance from the template, most on-template first —
 * `computeLeagueInsights` already sorts `templateScores` this way. `isYou`
 * marks the signed-in manager's own row directly, rather than this
 * component matching on a name, so it can't mismark a rival who happens to
 * share one. */
function TemplateSpread({ scores }: { scores: LeagueTemplateScore[] }) {
  if (scores.length === 0) return null;

  return (
    <div className="mt-lg">
      <h5>
        How the table matches the <Term term="template">template</Term>
      </h5>
      <div className="scroll-x">
        <table>
          <caption className="hint table-caption">
            Each manager's picks matching the league's template, out of their squad.
          </caption>
          <thead>
            <tr>
              <th scope="col">Team</th>
              <th scope="col">Manager</th>
              <th scope="col" className="num">
                Matches template
              </th>
            </tr>
          </thead>
          <tbody>
            {scores.map((score) => (
              <tr key={score.entryId} className={score.isYou ? 'league-row--own' : undefined}>
                <td>{score.teamName}</td>
                <td>
                  {score.managerName}
                  {score.isYou && <span className="pill pill--own">You</span>}
                </td>
                <td className="num">
                  {score.matchCount} of {score.squadSize}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
